import { useState, useEffect, useCallback } from 'react';
import { tournamentApi } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

export function useTournamentData(id, setToast) {
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTournamentData = useCallback(async () => {
    const parseJsonField = (field) => {
      if (!field) return [];
      if (Array.isArray(field)) return field;
      if (typeof field === 'string') {
        try {
          const parsed = JSON.parse(field);
          return Array.isArray(parsed) ? parsed : [];
        } catch { return []; }
      }
      return [];
    };

    const processByes = async (matchesList, tournamentData) => {
      if (!user || !tournamentData) return matchesList;
      const isOrg = user.role === 'admin' || tournamentData.created_by === user.id;
      if (!isOrg || !['registration', 'seeding', 'in_progress'].includes(tournamentData.status)) return matchesList;

      const pendingByes = matchesList.filter(m =>
        !m.winner_id &&
        m.notes && m.notes.toUpperCase().includes('BYE') &&
        (m.player1_id || m.player2_id)
      );

      if (pendingByes.length > 0) {
        try {
          setToast?.(`System: Automatically advancing ${pendingByes.length} player(s) with BYEs...`);
          await Promise.all(pendingByes.map(match =>
            tournamentApi.updateMatch(match.id, {
              winner_id: match.player1_id || match.player2_id,
              status: 'completed'
            })
          ));
          const refreshedRes = await tournamentApi.getMatches(id);
          return await processByes(refreshedRes.data, tournamentData);
        } catch (err) {
          console.error('Failed to auto-advance BYEs:', err);
        }
      }
      return matchesList;
    };

    try {
      setLoading(true);
      const [tournamentRes, playersRes, matchesRes] = await Promise.all([
        tournamentApi.getById(id),
        tournamentApi.getPlayers(id),
        tournamentApi.getMatches(id)
      ]);
      const tournamentData = tournamentRes.data;
      const processedTournament = {
        ...tournamentData,
        event_type: parseJsonField(tournamentData.event_type),
        event_category: parseJsonField(tournamentData.event_category),
        event_age_category: parseJsonField(tournamentData.event_age_category)
      };
      setTournament(processedTournament);
      setPlayers(playersRes.data);
      const processedMatches = await processByes(matchesRes.data, tournamentData);
      setMatches(processedMatches);

      // For league tournaments, also fetch standings
      if (tournamentData.draw_type === 'league') {
        try {
          const standingsRes = await tournamentApi.getStandings(id);
          setStandings(standingsRes.data);
        } catch {
          setStandings([]);
        }
      } else {
        setStandings([]);
      }
    } catch {
      setError('Failed to load tournament');
    } finally {
      setLoading(false);
    }
  }, [id, user, setToast]);

  useEffect(() => { loadTournamentData(); }, [loadTournamentData]);

  return { tournament, players, matches, standings, loading, error, reload: loadTournamentData };
}
